// Dismissing a popover on pointerdown leaves the matching click still in
// flight; without this it lands on whatever was under the pointer and
// activates it (a stream tile opens fullscreen, a market card opens its
// thread). Keep the guard for the lifetime of that pointer gesture so a long
// press cannot outlive a fixed timeout. Pointer cancellation or window blur
// clears it; pointerup schedules cleanup after the browser has dispatched the
// matching click.
export const CLICK_SURFACE_EXEMPT_SELECTOR = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="button"]',
    '[data-answer-interactive="true"]',
    '[data-no-modal-interaction]',
    '[data-ordinal-market-rail]',
].join(', ')

export function shouldSuppressSurfaceClick(target: EventTarget | null): boolean {
    const element = target as Element | null
    return !element?.closest?.(CLICK_SURFACE_EXEMPT_SELECTOR)
}

// A click surface's own handler should skip a click that nested chrome
// (a button, link, media shell...) inside it already owns.
export function surfaceClickIsClaimed(ev: {
    target: EventTarget | null
    currentTarget: EventTarget | null
}): boolean {
    const surface = ev.currentTarget as Element | null
    const hit = (ev.target as Element | null)?.closest?.(CLICK_SURFACE_EXEMPT_SELECTOR) || null
    return Boolean(hit && surface && hit !== surface && surface.contains(hit))
}

export function suppressNextClick(
    pointerDownEvent: Pick<PointerEvent, 'pointerId'>,
    shouldSuppress: (event: MouseEvent) => boolean = () => true,
): void {
    const pointerId = pointerDownEvent.pointerId
    let releaseCleanup: number | null = null

    const cleanup = () => {
        document.removeEventListener('click', handleClick, true)
        document.removeEventListener('pointerup', handlePointerUp, true)
        document.removeEventListener('pointercancel', handlePointerCancel, true)
        window.removeEventListener('blur', cleanup)
        if (releaseCleanup !== null) window.clearTimeout(releaseCleanup)
    }

    const handleClick = (event: MouseEvent) => {
        const clickPointerId = (event as PointerEvent).pointerId
        if (typeof clickPointerId === 'number' && clickPointerId !== pointerId) return
        if (shouldSuppress(event)) {
            event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation()
        }
        cleanup()
    }

    const handlePointerUp = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return
        // The native click follows pointerup in the same gesture. The next
        // task is only a backstop for gestures (for example a drag) that do
        // not produce a click.
        releaseCleanup = window.setTimeout(cleanup, 0)
    }

    const handlePointerCancel = (event: PointerEvent) => {
        if (event.pointerId === pointerId) cleanup()
    }

    document.addEventListener('click', handleClick, true)
    document.addEventListener('pointerup', handlePointerUp, true)
    document.addEventListener('pointercancel', handlePointerCancel, true)
    window.addEventListener('blur', cleanup)
}
